import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL } from '../../utils';

export default function AccesPages() {
  const [catalog, setCatalog] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const selected = accounts.find((account) => account.id === selectedId) || null;

  const groups = useMemo(() => {
    return catalog.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});
  }, [catalog]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/gestion-access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const nextAccounts = response.data.accounts || [];
      setCatalog(response.data.catalog || []);
      setAccounts(nextAccounts);
      setSelectedId((current) => current || nextAccounts.find((account) => !account.locked)?.id || nextAccounts[0]?.id || null);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Accès',
        text: error.response?.data?.message || 'Impossible de charger les accès.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setPages(selected?.pages || []);
  }, [selected]);

  const toggle = (url) => {
    setPages((current) => (
      current.includes(url) ? current.filter((item) => item !== url) : [...current, url]
    ));
  };

  const toggleGroup = (items) => {
    const urls = items.map((item) => item.url);
    const allOn = urls.every((url) => pages.includes(url));
    setPages((current) => (
      allOn
        ? current.filter((url) => !urls.includes(url))
        : [...new Set([...current, ...urls])]
    ));
  };

  const resetDefaults = async () => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Remettre les droits par défaut ?',
      text: 'Chaque compte retrouve les menus de son poste. Les accès déjà modifiés sont remplacés. Le super admin ne change pas.',
      showCancelButton: true,
      confirmButtonText: 'Remettre',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#15803d',
    });
    if (!confirm.isConfirmed) return;

    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${APP_API_URL}/gestion-access/install`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const refreshed = await axios.get(`${APP_API_URL}/gestion-access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const nextAccounts = refreshed.data.accounts || [];
      setCatalog(refreshed.data.catalog || []);
      setAccounts(nextAccounts);
      Swal.fire({
        icon: 'success',
        title: 'Droits par défaut',
        text: response.data?.message || 'Les menus du poste sont rétablis.',
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Impossible de rétablir les droits.',
      });
    } finally {
      setResetting(false);
    }
  };

  const save = async () => {
    if (!selected || selected.locked) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${APP_API_URL}/gestion-access/${selected.id}`, { pages }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccounts((current) => current.map((account) => (
        account.id === selected.id ? { ...account, pages, custom: true } : account
      )));
      Swal.fire({
        icon: 'success',
        title: 'Accès enregistrés',
        text: `${selected.name} verra ces menus à sa prochaine connexion.`,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Enregistrement impossible.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout loading={loading}>
      <div className="mx-auto w-full max-w-6xl pb-8">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="mb-1 text-xl font-bold text-gray-900">Accès aux pages</h1>
            <p className="text-sm text-gray-600">
              Chaque compte a déjà les menus de son poste. Cochez ou retirez des pages, puis enregistrez.
            </p>
          </div>
          <button
            type="button"
            onClick={resetDefaults}
            disabled={resetting || loading}
            className="rounded-md border border-green-700 px-4 py-2 text-sm font-semibold text-green-800 disabled:opacity-60"
          >
            {resetting ? 'Rétablissement...' : 'Remettre les droits par défaut'}
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            {accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => setSelectedId(account.id)}
                className={`mb-2 w-full rounded-md px-3 py-2 text-left ${
                  account.id === selectedId ? 'bg-green-700 text-white' : 'bg-gray-50 text-gray-800 hover:bg-gray-100'
                }`}
              >
                <span className="block text-sm font-semibold">{account.name}</span>
                <span className={`block text-xs ${account.id === selectedId ? 'text-green-100' : 'text-gray-500'}`}>
                  {account.poste || account.role}
                  {account.locked ? ' · accès complet' : ''}
                </span>
              </button>
            ))}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            {selected?.locked ? (
              <p className="text-sm text-gray-600">Le super admin garde tous les menus.</p>
            ) : (
              <>
                {Object.entries(groups).map(([category, items]) => (
                  <section key={category} className="mb-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h2 className="text-sm font-bold text-gray-800">{category}</h2>
                      <button
                        type="button"
                        className="text-xs font-semibold text-green-700"
                        onClick={() => toggleGroup(items)}
                      >
                        Tout cocher
                      </button>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {items.map((item) => (
                        <div key={item.url} className="flex items-center gap-2 rounded-md border border-gray-100 px-3 py-2 text-sm">
                          <input
                            type="checkbox"
                            checked={pages.includes(item.url)}
                            onChange={() => toggle(item.url)}
                            aria-label={item.text}
                          />
                          <i className={item.icon} />
                          <Link
                            to={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="min-w-0 flex-1 truncate font-medium text-green-800 hover:underline"
                            title={`Ouvrir ${item.text}`}
                          >
                            {item.text}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !selected}
                  className="rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer les accès'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
